import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Bot, Workflow, Users, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import Sparkline from "@/components/Sparkline";

/**
 * AI Usage rollup section for the Billing page.
 *
 * Aggregates Phase A (agent_runs) + Phase C (flow_runs) by tenant + flow + agent.
 * Sits BELOW the legacy form-generation request_logs section (preserved as-is).
 */
export default function AiUsageSection({ dateFrom, dateTo, clientId }) {
    const [overall, setOverall] = useState(null);
    const [byClient, setByClient] = useState([]);
    const [byFlow, setByFlow] = useState([]);
    const [byAgent, setByAgent] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.set("date_from", `${dateFrom}T00:00:00+00:00`);
            if (dateTo) params.set("date_to", `${dateTo}T23:59:59+00:00`);
            if (clientId) params.set("client_id", clientId);
            const qs = params.toString();
            const [o, c, f, a] = await Promise.all([
                api.get(`/admin/billing/usage?${qs}`),
                api.get(`/admin/billing/usage/by-client?${qs}`),
                api.get(`/admin/billing/usage/by-flow?${qs}`),
                api.get(`/admin/billing/usage/by-agent?${qs}`),
            ]);
            setOverall(o.data);
            setByClient(c.data.items || []);
            setByFlow(f.data.items || []);
            setByAgent(a.data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load AI usage");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [dateFrom, dateTo, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const exportCsv = () => {
        const params = new URLSearchParams();
        if (dateFrom) params.set("date_from", `${dateFrom}T00:00:00+00:00`);
        if (dateTo) params.set("date_to", `${dateTo}T23:59:59+00:00`);
        if (clientId) params.set("client_id", clientId);
        params.set("limit", "10000");
        const token = localStorage.getItem("tf_token");
        fetch(`${API}/admin/billing/usage/events.csv?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.ok ? r.blob() : Promise.reject(new Error("Export failed")))
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ai-usage-${dateFrom || "all"}_${dateTo || "all"}${clientId ? "_" + clientId : ""}.csv`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("CSV downloaded");
            })
            .catch((e) => toast.error(e.message));
    };

    const totals = overall?.totals;
    const byMonth = overall?.by_month || [];

    return (
        <div className="space-y-5 mt-10" data-testid="ai-usage-section">
            <div className="flex items-end justify-between">
                <div>
                    <div className="label-xs text-[#7C3AED]">AI Operations</div>
                    <h2 className="font-display text-2xl tracking-tight mt-2">AI Usage rollup</h2>
                    <p className="text-xs text-white/50 mt-1">
                        Aggregates flow runs + agent runs across all tenants on this server only.
                    </p>
                </div>
                <button onClick={exportCsv} data-testid="ai-usage-export"
                    aria-label="Export AI usage events as CSV"
                    className="px-3 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Export events.csv
                </button>
            </div>

            {loading && <div className="text-white/40 text-xs">Loading…</div>}

            {totals && (
                <div className="grid grid-cols-5 gap-3" data-testid="ai-usage-kpis">
                    <Kpi label="Total calls" value={totals.calls.toLocaleString()} mono />
                    <Kpi label="AI cost" value={`$${totals.cost_usd.toFixed(5)}`} mono accent />
                    <Kpi label="Flow runs" value={totals.flow_calls.toLocaleString()} mono color="text-cyan-400" />
                    <Kpi label="Agent runs" value={totals.agent_calls.toLocaleString()} mono color="text-emerald-400" />
                    <Kpi label="Tokens (in / out)"
                         value={`${(totals.tokens_in / 1000).toFixed(1)}k / ${(totals.tokens_out / 1000).toFixed(1)}k`} mono />
                </div>
            )}

            {byMonth.length > 0 && (
                <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4" data-testid="ai-usage-by-month">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-3.5 h-3.5 text-[#7C3AED]" />
                        <div className="font-display text-sm">Monthly trend</div>
                    </div>
                    <Sparkline
                        points={byMonth.map((m) => ({ bucket: m.key, calls: m.calls, cost_usd: m.cost_usd }))}
                        metric="cost"
                        color="#7C3AED"
                        height={48}
                        showAxis
                    />
                    <div className="grid grid-cols-12 gap-2 mt-3">
                        {byMonth.slice(-12).map((m) => (
                            <div key={m.key} className="text-center">
                                <div className="font-mono text-[9px] text-white/40">{m.key}</div>
                                <div className="font-mono text-[10px] text-white/80">${m.cost_usd.toFixed(5)}</div>
                                <div className="font-mono text-[9px] text-[#7C3AED]">{m.calls}c</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-3 gap-3">
                <BreakdownCard
                    title="Per-client"
                    icon={<Users className="w-3.5 h-3.5 text-[#7C3AED]" />}
                    items={byClient}
                    keyName="key"
                    testId="ai-usage-by-client"
                />
                <BreakdownCard
                    title="Per-flow"
                    icon={<Workflow className="w-3.5 h-3.5 text-cyan-400" />}
                    items={byFlow}
                    keyName="flow_slug"
                    testId="ai-usage-by-flow"
                />
                <BreakdownCard
                    title="Per-agent"
                    icon={<Bot className="w-3.5 h-3.5 text-emerald-400" />}
                    items={byAgent}
                    keyName="agent_slug"
                    testId="ai-usage-by-agent"
                />
            </div>
        </div>
    );
}

function BreakdownCard({ title, icon, items, keyName, testId }) {
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md" data-testid={testId}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">{icon}<div className="font-display text-sm">{title}</div></div>
                <div className="text-[10px] text-white/40">{items.length}</div>
            </div>
            <div className="max-h-72 overflow-y-auto">
                {items.length === 0 ? (
                    <div className="p-6 text-center text-xs text-white/40">No activity in range.</div>
                ) : items.slice(0, 50).map((it) => (
                    <div key={`${it[keyName]}-${it.flow_id || it.agent_id || ""}`}
                         className="px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.02]">
                        <div className="flex items-center justify-between text-xs">
                            <div className="font-medium truncate max-w-[60%]">{it[keyName] || "(unknown)"}</div>
                            <div className="font-mono text-[#7C3AED]">${(it.cost_usd || 0).toFixed(5)}</div>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                            <div className="font-mono text-[10px] text-white/40">{it.calls} calls</div>
                            <div className="font-mono text-[10px] text-white/40">
                                {it.tokens_in ? `${(it.tokens_in / 1000).toFixed(1)}k in` : ""}
                                {it.tokens_out ? ` / ${(it.tokens_out / 1000).toFixed(1)}k out` : ""}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Kpi({ label, value, accent, mono, color }) {
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-4">
            <div className="label-xs">{label}</div>
            <div className={`mt-1.5 text-base ${mono ? "font-mono" : ""} ${color || (accent ? "text-[#7C3AED]" : "text-white/90")}`}>
                {value}
            </div>
        </div>
    );
}
